import React, { useState, useCallback, useMemo } from "react";
import {
  TextField, Button, Typography, Box, Tooltip, IconButton, CircularProgress,
  Grid, Paper, Chip, Stack, Divider, List, ListItem, ListItemAvatar, Avatar,
  ListItemText, Alert, AlertTitle
} from "@mui/material";
import {
  FaPlusCircle, FaGoogleDrive
} from "react-icons/fa";
import {
  PictureAsPdf as PdfIcon,
  Description as DescriptionIcon,
  Article as ArticleIcon,
  Slideshow as PptIcon,
  TableChart as SheetIcon,
  DataArray as JsonIcon,
  Code as CodeIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  InsertDriveFile as FileIcon,
  // Action Icons
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useDropzone, Accept } from 'react-dropzone';
import { useApp, useAuth } from "../../hooks";
import {
  createDatabaseFromFileService,
  getDatabasesService,
  getGoogleAuthToken,
  get_upload_url
} from "../../services/databases.service";
// import DatabasesList from "./DatabasesList";
import useDrivePicker from 'react-google-drive-picker';
import { v4 as uuidv4 } from 'uuid';

const supportedExtensions = [
  'pdf', 'docx', 'ppt', 'pptx', 'txt', 'xlsx', 'png',
  'jpg', 'jpeg', 'csv',  'xml', 'html', 'doc', 'md',
  'mp4',
  // Coding files
  'js', 'py', 'java', 'cpp', 'cc', 'cxx', 'c', 'cs',
  'php', 'swift', 'ts', 'go'

];
const supportedExtensionsString = supportedExtensions.map(ext => `.${ext}`).join(', ');

const MAX_TOTAL_FILES = 30;
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;

const getFileIcon = (extension: string): React.ReactNode => {
  switch (extension?.toLowerCase()) {
    case 'pdf': return <PdfIcon />;
    case 'doc':
    case 'docx': return <DescriptionIcon />;
    case 'ppt':
    case 'pptx': return <PptIcon />;
    case 'xls':
    case 'xlsx': return <SheetIcon />;
    case 'csv': return <SheetIcon />;
    case 'png':
    case 'jpg':
    case 'jpeg': return <ImageIcon />;
    case 'txt': return <ArticleIcon />;
    case 'json': return <JsonIcon />;
    case 'xml':
    case 'html': return <CodeIcon />;
    case 'md': return <ArticleIcon />;
    case 'mp4': return <VideoIcon />;
    case 'js':
    case 'ts':
      return <CodeIcon />;
    case 'css':
    case 'scss':
      return <CodeIcon />;
    case 'java':
    case 'py':
    case 'cpp':
    case 'c':
      return <CodeIcon />;
    case 'yaml':
    case 'yml':
      return <CodeIcon />;
    case 'sql':
      return <CodeIcon />;
    default: return <FileIcon />;
  }
};

const generateAcceptObject = (extensions: string[]): Accept => {
  const mimeMap: { [key: string]: string[] } = {
    'pdf': ['application/pdf'],
    'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    'ppt': ['application/vnd.ms-powerpoint'],
    'pptx': ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    'txt': ['text/plain'],
    'xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    'png': ['image/png'],
    'jpg': ['image/jpeg'],
    'jpeg': ['image/jpeg'],
    'csv': ['text/csv'],
    'xml': ['application/xml', 'text/xml'],
    'html': ['text/html'],
    'doc': ['application/msword'],
    'md': ['text/markdown', 'text/plain'],
    'mp4': ['video/mp4'],
    // Coding files MIME types
    'js': ['application/javascript', 'text/javascript'],
    'py': ['application/x-python-code', 'text/x-python'],
    'java': ['text/x-java-source', 'text/java'],
    'cpp': ['text/x-c++src', 'text/x-c++'],
    'cc': ['text/x-c++src', 'text/x-c++'],
    'cxx': ['text/x-c++src', 'text/x-c++'],
    'c': ['text/x-csrc', 'text/x-c'],
    'cs': ['text/x-csharp', 'application/x-csharp'],
    'php': ['application/x-httpd-php', 'text/x-php'],
    // 'rb': ['text/x-ruby', 'application/x-ruby'],
    'swift': ['text/x-swift', 'application/x-swift'],
    'ts': ['application/typescript', 'text/typescript'],
    'go': ['text/x-go', 'application/x-go']
  };

  const acceptObj: { [key: string]: string[] } = {};
  extensions.forEach(ext => {
    const mimeTypes = mimeMap[ext] || [];
    const extensionKey = `.${ext}`;

    if (mimeTypes.length > 0) {
      mimeTypes.forEach((mime: string) => {
        if (!acceptObj[mime]) {
          acceptObj[mime] = [];
        }
        if (Array.isArray(acceptObj[mime])) {
          acceptObj[mime].push(extensionKey);
        }
      });
    } else {
      if (!acceptObj[extensionKey]) {
        acceptObj[extensionKey] = [];
      }
    }
  });
  return acceptObj;
};

interface LocalFileMeta {
  type: string;
  name: string;
  size: number;
  extension: string;
}

interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  lastEditedUtc?: string;
  lastModifiedDate?: string;
  sizeBytes?: number;
}


const DatabaseContentForm = ({ setTabs }: any) => {
  const {state, dispatch}: any = useApp();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors]: any = useState({});
  const { auth }: any = useAuth();
  const [database, setDatabase] = useState({ database_name: '' });
  const [localFilesMeta, setLocalFilesMeta] = useState<LocalFileMeta[]>([]);
  const [localFilesData, setLocalFilesData] = useState<File[]>([]);
  const [googleDriveFiles, setGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [fileError, setFileError] = useState('');
  const [customInstructions, setCustomInstructions] = useState(['']);

  const acceptFileTypes = useMemo(() => generateAcceptObject(supportedExtensions), []);

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: any[]) => {
    setFileError('');
    let newLocalFilesMeta: LocalFileMeta[] = [];
    let newLocalFilesData: File[] = [];
    let rejectedMessages: string[] = [];
    let currentTotalFiles = localFilesMeta.length + googleDriveFiles.length;
    const currentVideoCount = localFilesMeta.filter(f => f.extension === 'mp4').length +
                              googleDriveFiles.filter(f => f.mimeType === 'video/mp4').length;
    let newVideoCount = currentVideoCount;
    console.log(newLocalFilesMeta);
    console.log("Accepted files: ", acceptedFiles);

    if (currentTotalFiles + acceptedFiles.length > MAX_TOTAL_FILES) {
      setFileError(`Maximum ${MAX_TOTAL_FILES} files allowed. You currently have ${currentTotalFiles} and tried to add ${acceptedFiles.length}. Please remove some files or add fewer files.`);
      acceptedFiles.forEach(file => {
        rejectedMessages.push(`${file.name}: Exceeds total file limit of ${MAX_TOTAL_FILES}.`);
      });
      acceptedFiles = [];
    } else {
      acceptedFiles.forEach((file: File) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

        // Video file count check
        if (fileExtension === 'mp4') {
          newVideoCount += 1;
          if (newVideoCount > 3) {
            rejectedMessages.push(`${file.name}: Only 3 video files are allowed.`);
            return;
          }
        }

        const fileLimit = fileExtension === 'mp4' ? 100 * 1024 * 1024 : MAX_FILE_SIZE_BYTES;
        console.log("File limit: ", fileLimit);
        if (file.size > fileLimit) {
          rejectedMessages.push(`${file.name}: File exceeds the ${fileExtension === 'mp4' ? '100' : '15'}MB size limit.`);
          return;
        }

        if (supportedExtensions.includes(fileExtension)) {
          newLocalFilesMeta.push({
            type: file.type || 'application/octet-stream',
            name: file.name,
            size: file.size,
            extension: fileExtension
          });
          newLocalFilesData.push(file);

          if (fileExtension === 'mp4') {
            const wasVideoPresent = localFilesMeta.some(f => f.extension === 'mp4') || googleDriveFiles.some(f => f.mimeType === 'video/mp4');
            if (!wasVideoPresent) {
              setCustomInstructions(['']);
            }
          }
        } else {
          rejectedMessages.push(`${file.name}: Unsupported file type (.${fileExtension}).`);
        }
      });
    }


    fileRejections.forEach((rejection: any) => {
      const errorMessages = rejection.errors.map((e: any) => {
        if (e.code === 'file-too-large') {
          return `File is larger than allowed size`;
        }
        if (e.code === 'file-invalid-type') {
          return `Invalid file type`;
        }
        if (e.code === 'too-many-files') {
          return `Cannot add this many files at once (limit: ${MAX_TOTAL_FILES} total)`;
        }
        return e.message;
      }).join(', ');
      rejectedMessages.push(`${rejection.file.name}: ${errorMessages}`);
    });

    if (rejectedMessages.length > 0) {
      const existingError = fileError ? fileError + '\n' : '';
      setFileError(`${existingError}Some files were rejected:\n- ${rejectedMessages.join('\n- ')}\n\nSupported types: ${supportedExtensions.join(', ')}\nMax file size: 15MB (100MB for MP4)`);
    }

    if (newLocalFilesMeta.length > 0 && (currentTotalFiles + newLocalFilesMeta.length <= MAX_TOTAL_FILES)) {
      setLocalFilesMeta((prev: LocalFileMeta[]) => [...prev, ...newLocalFilesMeta]);
      setLocalFilesData((prev: File[]) => [...prev, ...newLocalFilesData]);
    } else if (newLocalFilesMeta.length > 0 && (currentTotalFiles + newLocalFilesMeta.length > MAX_TOTAL_FILES)) {
      console.warn("Prevented adding files exceeding total limit after initial check.");
    }


  }, [localFilesMeta, googleDriveFiles, supportedExtensions, fileError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptFileTypes,
    maxFiles: MAX_TOTAL_FILES,
    disabled: loading,
  });

  const handledatabaseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{}`~;':"\\|,.<>\/?]+/;

    if (name === 'database_name') {
      if (specialCharRegex.test(value)) {
        setErrors((prev: any) => ({ ...prev, [name]: 'Drive name should not contain special characters' }));
      } else {
        setErrors((prev: any) => ({ ...prev, [name]: '' }));
      }
      setDatabase({ ...database, [name]: value });
    } else {
      setDatabase({ ...database, [name]: value });
    }
  };

  const handleCustomInstructionChange = (index: number, value: string) => {
    const newInstructions = [...customInstructions];
    newInstructions[index] = value.slice(0, 100);
    setCustomInstructions(newInstructions);
  };

  const addCustomInstructionField = () => {
    if (customInstructions.length < 3) {
      setCustomInstructions([...customInstructions, '']);
    }
  };

  const handleRemoveLocalFile = (index: number) => {
    const newFilesMeta = [...localFilesMeta];
    const newFilesData = [...localFilesData];
    newFilesMeta.splice(index, 1);
    newFilesData.splice(index, 1);
    setLocalFilesMeta(newFilesMeta);
    setLocalFilesData(newFilesData);
    if (newFilesMeta.length + googleDriveFiles.length <= MAX_TOTAL_FILES) {
      setFileError('');
    } else {
      setFileError(`Maximum ${MAX_TOTAL_FILES} files allowed. You currently have ${newFilesMeta.length + googleDriveFiles.length}.`);
    }
  };

  const handleRemoveGoogleDriveFile = (index: number) => {
    const newGoogleDriveFiles = [...googleDriveFiles];
    newGoogleDriveFiles.splice(index, 1);
    setGoogleDriveFiles(newGoogleDriveFiles);
    if (localFilesMeta.length + newGoogleDriveFiles.length <= MAX_TOTAL_FILES) {
      setFileError('');
    } else {
      setFileError(`Maximum ${MAX_TOTAL_FILES} files allowed. You currently have ${localFilesMeta.length + newGoogleDriveFiles.length}.`);
    }
  };

  const hasFiles = useMemo(() => localFilesMeta.length > 0 || googleDriveFiles.length > 0, [localFilesMeta, googleDriveFiles]);

  const hasVideoFile = useMemo(() =>
          localFilesMeta.some((f: LocalFileMeta) => f.extension === 'mp4') ||
          googleDriveFiles.some((f: GoogleDriveFile) => f.mimeType === 'video/mp4'),
      [localFilesMeta, googleDriveFiles]);


  const validateForm = (): boolean => {
    let tempErrors: any = {};
    let isValid = true;
    const currentTotalFiles = localFilesMeta.length + googleDriveFiles.length;

    const trimmedName = database.database_name.trim();
    if (!trimmedName) {
      isValid = false; tempErrors.database_name = 'Please enter a Drive name.';
    }
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{}`~;':"\\|,.<>\/?]+/;
    if (specialCharRegex.test(trimmedName)) {
      isValid = false; tempErrors.database_name = 'Drive name should not contain special characters.';
    }

    if (currentTotalFiles === 0) {
      isValid = false;
      if (!fileError) setFileError('Please select or drop at least one file.');
    }
    else if (currentTotalFiles > MAX_TOTAL_FILES) {
      isValid = false;
      if (!fileError?.includes(`Maximum ${MAX_TOTAL_FILES} files allowed`)) {
        setFileError(prev => `${prev ? prev + '\n' : ''}Maximum ${MAX_TOTAL_FILES} files allowed. You currently have ${currentTotalFiles}.`);
      }
    }
    else if (fileError) {
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };


  const [openPicker, authResponse] = useDrivePicker();
  const handleOpenPicker = () => {
    setFileError('');
    getGoogleAuthToken(auth.user.uid, state.selectedOrganisation._id).then((token: string | null) => {
      if (!token) {
        dispatch({
          type: 'SET_NOTIFICATION',
          payload: { show: true, title: 'Google Drive Error', message: 'Could not authenticate with Google Drive. Please ensure permissions are granted or try again.', type: 'error' }
        });
        return;
      }

      const googleDriveMimeTypes = [
        'application/pdf', // .pdf
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/vnd.google-apps.document', // Google Docs
        // 'application/vnd.ms-excel', // .xls (Often less reliable, GSheets preferred)
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.google-apps.spreadsheet', // Google Sheets
        // 'application/vnd.ms-powerpoint', // .ppt
        'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
        'application/vnd.google-apps.presentation', // Google Slides
        'text/plain', // .txt
        'text/csv', // .csv
        'text/xml', // .xml
        'application/xml', // .xml
        'text/html', // .html
        'text/markdown', // .md
        'image/jpeg', // .jpg, .jpeg
        'image/png', // .png
        'video/mp4', // .mp4
        // code files
        'text/javascript', // .js
        'application/javascript', // .js
        'application/x-python-code', // .py
        'text/x-java-source', // .java
        'text/x-c++src', // .cpp
        'text/x-csrc', // .c
        'text/x-csharp', // .cs
        'application/x-httpd-php', // .php
        // 'text/x-ruby', // .rb
        'text/x-swift', // .swift
        'application/typescript', // .ts
        'text/x-go', // .go

      ].join(',');

      const pickerConfig: any = {
        clientId: process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID,
        developerKey: process.env.REACT_APP_GOOGLE_OAUTH_DEVELOPER_KEY,
        viewMimeTypes: googleDriveMimeTypes,
        token: token,
        appId: process.env.REACT_APP_GOOGLE_OAUTH_APP_ID || '',
        showUploadView: true,
        showUploadFolders: true,
        supportDrives: true,
        setIncludeFolders: true,
        multiselect: true,
        maxItems: MAX_TOTAL_FILES,
        customScopes: ['https://www.googleapis.com/auth/drive.readonly'],
        callbackFunction: (data: any) => {
          const elements = Array.from(document.getElementsByClassName('picker-dialog') as HTMLCollectionOf<HTMLElement>);
          for (let i = 0; i < elements.length; i++) {
            elements[i].style.zIndex = '2000';
          }

          if (data.action === 'picked') {
            const pickedFiles = data.docs.filter((doc: GoogleDriveFile) => doc.mimeType !== 'application/vnd.google-apps.folder');
            onSelectFileFromGoogleDrive(pickedFiles);
          } else if (data.action === 'cancel') {
            console.log('Google Drive Picker cancelled by user.');
          } else {
            console.log('Google Drive Picker action:', data.action, data);
          }
        },
      };
      openPicker(pickerConfig);
    }).catch((err: any) => {
      console.error("Error getting Google Auth Token or opening Picker:", err);
      dispatch({
        type: 'SET_NOTIFICATION',
        payload: { show: true, title: 'Google Drive Error', message: 'Error preparing Google Drive selection. Please try again.', type: 'error' }
      });
    });
  };

  const onSelectFileFromGoogleDrive = (googleDocs: GoogleDriveFile[]) => {
    console.log("Google docs selected (files only):", googleDocs);
    const currentTotalFiles = localFilesMeta.length + googleDriveFiles.length;
    const filesSelectedCount = googleDocs.length;
    const currentVideoCount = localFilesMeta.filter(f => f.extension === 'mp4').length +
                              googleDriveFiles.filter(f => f.mimeType === 'video/mp4').length;
    let newVideoCount = currentVideoCount;

    let validFilesToAdd: GoogleDriveFile[] = [];
    let rejectedMessages: string[] = [];

    if (currentTotalFiles + filesSelectedCount > MAX_TOTAL_FILES) {
      const countErrorMsg = `Maximum ${MAX_TOTAL_FILES} files allowed. You have ${currentTotalFiles} and selected ${filesSelectedCount} Google Drive file(s), exceeding the limit. No Google Drive files from this selection were added.`;
      rejectedMessages.push(countErrorMsg);
    } else {
      googleDocs.forEach(file => {
        // Video file count check
        if (file.mimeType === 'video/mp4') {
          newVideoCount += 1;
          if (newVideoCount > 3) {
            rejectedMessages.push(`${file.name}: Only 3 video files are allowed.`);
            return;
          }
        }
        const isVideo = file.mimeType === 'video/mp4';
        const maxSize = isVideo ? 100 * 1024 * 1024 : MAX_FILE_SIZE_BYTES;
        if (file.sizeBytes && file.sizeBytes > maxSize) {
          rejectedMessages.push(`${file.name}: Exceeds the ${isVideo ? '100' : '15'}MB size limit.`);
        } else if (
          !file.sizeBytes &&
          ![
            'application/vnd.google-apps.document',
            'application/vnd.google-apps.spreadsheet',
            'application/vnd.google-apps.presentation',
            'video/mp4'
          ].includes(file.mimeType)
        ) {
          rejectedMessages.push(`${file.name}: Size is unknown and cannot be verified.`);
        }
        else {
          validFilesToAdd.push(file);
        }
      });
    }

    if (rejectedMessages.length > 0) {
      setFileError(rejectedMessages.join('\n'));
    }

    if (validFilesToAdd.length > 0) {
      setGoogleDriveFiles((prev: GoogleDriveFile[]) => [...prev, ...validFilesToAdd]);

      const isAddingVideo = validFilesToAdd.some(f => f.mimeType === 'video/mp4');
      const wasVideoPresentBefore =
        localFilesMeta.some(f => f.extension === 'mp4') ||
        googleDriveFiles.some(f => f.mimeType === 'video/mp4');

      if (isAddingVideo && !wasVideoPresentBefore) {
        setCustomInstructions(['']);
      }
    } else if (googleDocs.length > 0 && validFilesToAdd.length === 0) {
      console.log("All selected Google Drive files were rejected.");
    }
  };
  // --- End Google Drive Picker Logic ---

  // --- onSave Logic ---
  const onSave = async () => {
    if (!validateForm()) {
      dispatch({
        type: 'SET_NOTIFICATION',
        payload: { show: true, title: 'Drive Creation Failed', message: 'Please fix the errors highlighted in the form and file list.', type: 'error', toastId: 'database-creation-error', autoClose: 5000 }
      });
      return;
    }

    console.log("Starting drive creation process...");

    setLoading(true);
    dispatch({
      type: 'SET_NOTIFICATION',
      payload: {
        show: true, title: 'Creating Drive',
        message: 'Preparing to process files and create your drive...',
        type: 'info', toastId: 'database-progress', autoClose: false
      }
    });

    try {
      const database_id = uuidv4();
      const payload: any = {
        database_name: database.database_name.trim(),
        organisation_id: state.selectedOrganisation._id,
        custom_instructions: (hasVideoFile && customInstructions.some(inst => inst.trim() !== ''))
            ? customInstructions.filter(instruction => instruction.trim() !== '')
            : [],
        database_id: database_id,
        file_ids: []
      };
      let fileProcessingError = false;

      if (localFilesData.length > 0) {
        dispatch({
          type: 'SET_NOTIFICATION',
          payload: { show: true, title: 'Creating Drive', message: `We are processing your files, this may take sometime.`, type: 'info', toastId: 'database-progress', updateType: 'progress' }
        });

        for (let i = 0; i < localFilesMeta.length; i++) {
          const file_id = uuidv4();
          try {
            const isMp4 = localFilesMeta[i].extension.toLowerCase() === 'mp4';
            const maxServerFileSize = isMp4 ? 100 * 1024 * 1024 : MAX_FILE_SIZE_BYTES;
            if (localFilesData[i].size > maxServerFileSize) {
              throw new Error(`File ${localFilesMeta[i].name} exceeds the ${isMp4 ? '100' : '15'}MB size limit.`);
            }

            const { upload_url_status, upload_url } = await get_upload_url(
                state.selectedOrganisation._id, database_id, null,
                localFilesMeta[i].extension,
                file_id
            );

            if (upload_url_status && upload_url) {
              const response = await fetch(upload_url, {
                method: 'PUT',
                body: localFilesData[i],
                headers: { 'Content-Type': localFilesMeta[i].type || 'application/octet-stream' },
              });

              if (!response.ok) {
                let errorBody = 'Upload failed';
                try { errorBody = await response.text(); } catch (_) {}
                throw new Error(`Upload failed for ${localFilesMeta[i].name} (Status: ${response.status}). ${errorBody}`);
              } else {
                payload.file_ids.push({
                  file_id: file_id,
                  file_type: localFilesMeta[i].extension,
                  file_name: localFilesMeta[i].name,
                  lastEditedUtc: new Date().toISOString(),
                  is_google_drive: false
                });
              }
            } else {
              throw new Error(`Could not get upload URL for ${localFilesMeta[i].name}.`);
            }
          } catch (uploadError: any) {
            console.error("Upload Error:", uploadError);
            dispatch({
              type: 'SET_NOTIFICATION',
              payload: { show: true, title: 'Upload Error', message: `Failed to upload ${localFilesMeta[i]?.name || 'a file'}. ${uploadError.message}`, type: 'info', toastId: 'database-progress', updateType: 'error', autoClose: false }
            });
            fileProcessingError = true;
            break;
          }
        }
      }

      if (fileProcessingError) {
        setLoading(false);
        return;
      }

      if (googleDriveFiles.length > 0) {
        googleDriveFiles.forEach((gfile: GoogleDriveFile) => {
          const isVideo = gfile.mimeType === 'video/mp4';
          const maxSize = isVideo ? 100 * 1024 * 1024 : MAX_FILE_SIZE_BYTES;
          if (gfile.sizeBytes && gfile.sizeBytes > maxSize) {
            console.warn(`Skipping Google Drive file ${gfile.name} due to size limit.`);
            dispatch({
              type: 'SET_NOTIFICATION',
              payload: { show: true, title: 'File Skipped', message: `Google Drive file "${gfile.name}" was skipped as it exceeds the size limit.`, type: 'warning', duration: 6000 }
            });
            return;
          }

          payload.file_ids.push({
            file_id: gfile.id,
            file_type: gfile.mimeType,
            file_name: gfile.name,
            lastEditedUtc: String(gfile.lastEditedUtc) || (gfile.lastModifiedDate ? new Date(gfile.lastModifiedDate).toISOString() : new Date().toISOString()),
            is_google_drive: true
          });
        });
      }


      if (payload.file_ids.length === 0) {
        dispatch({
          type: 'SET_NOTIFICATION',
          payload: { show: true, title: 'Drive Creation Failed', message: 'No valid files were found to add to the drive. Please check file sizes and try again.', type: 'info', toastId: 'database-progress', updateType: 'error', autoClose: false }
        });
        setLoading(false);
        return;
      }

      dispatch({
        type: 'SET_NOTIFICATION',
        payload: { show: true, title: 'Creating Drive', message: 'We are processing your files, this may take some time.', type: 'info', toastId: 'database-progress', updateType: 'info' }
      });

      const { status, message } = await createDatabaseFromFileService(payload);

      if (status) {
        dispatch({
          type: 'SET_NOTIFICATION',
          payload: { show: true, title: 'Drive Created', message: `Drive "${payload.database_name}" created successfully.`, type: 'success', toastId: 'database-progress', updateType: 'success', autoClose: 8000 } // Keep success message a bit longer
        });
        setDatabase({ database_name: '' });
        setLocalFilesMeta([]);
        setLocalFilesData([]);
        setGoogleDriveFiles([]);
        setCustomInstructions(['']);
        setErrors({});
        setFileError('');

        if (state.selectedOrganisation?._id) {
          try {
            setTimeout(async () => {
              const databases = await getDatabasesService(state.selectedOrganisation._id, null, null, null, null);
              if (databases && databases.response) {
                dispatch({ type: 'SET_DATABASES', payload: databases.response });
              }
            }, 1000);
          } catch (fetchError) {
            console.error("Failed to refresh databases list:", fetchError);
          }
        }
      } else {
        dispatch({
          type: 'SET_NOTIFICATION',
          payload: { show: true, title: 'Drive Creation Failed', message: message || 'An error occurred on the server while finalizing the drive.', type: 'info', toastId: 'database-progress', updateType: 'error', autoClose: false } // Keep error message visible until dismissed
        });
      }

    } catch (error: any) {
      console.error("Save Operation Error:", error);
      dispatch({
        type: 'SET_NOTIFICATION',
        payload: { show: true, title: 'Error', message: `An unexpected error occurred: ${error.message || 'Please try again.'}`, type: 'info', toastId: 'database-progress', updateType: 'error', autoClose: false } // Keep error message visible
      });
    } finally {
      setLoading(false);
    }
  };
  // --- End onSave Logic ---


  return (
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, maxWidth: 700, mx: 'auto' }}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
          Create New Knowledge Drive
        </Typography>
        <Box component="form" noValidate autoComplete="off" onSubmit={(e) => { e.preventDefault(); onSave(); }}>

          <TextField
              label="Drive Name"
              fullWidth
              margin="normal"
              name="database_name"
              value={database.database_name}
              onChange={handledatabaseChange}
              error={!!errors['database_name']}
              helperText={errors['database_name'] || "Enter a unique name for this knowledge drive."}
              required
              sx={{ mb: 3 }}
              disabled={loading}
          />

          <Typography variant="h6" gutterBottom>
            Upload Files
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Box
                  {...getRootProps()}
                  sx={{
                    border: `2px dashed ${isDragActive ? 'primary.main' : (fileError ? 'error.main' : 'grey.400')}`,
                    borderRadius: 1, p: 3, textAlign: 'center', cursor: 'pointer',
                    backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                    height: '100%', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    opacity: loading ? 0.5 : 1
                  }}
              >
                <input {...getInputProps()} disabled={loading} />
                <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                {isDragActive ? (
                    <Typography>Drop the files here ...</Typography>
                ) : (
                    <Typography>Drag 'n' drop files here, or click to select</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, px: 1 }}>
                  Supports: {supportedExtensionsString} (Max {MAX_TOTAL_FILES} files total, 15MB per file, 100MB for MP4)
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip title={`Select files directly from your Google Drive (Max ${MAX_FILE_SIZE_MB}MB per file). Folders cannot be selected.`}>
                    <span>
                      <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<FaGoogleDrive />}
                          onClick={handleOpenPicker}
                          fullWidth
                          sx={{ py: 1.5 }}
                          disabled={loading}
                      >
                            Select from Google Drive
                        </Button>
                     </span>
              </Tooltip>
            </Grid>
          </Grid>

          {fileError && (
              <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                <AlertTitle>File Error(s)</AlertTitle>
                {fileError}
              </Alert>
          )}

          {hasFiles && (
              <Box sx={{ mb: 3 }}>
                <Divider sx={{ my: 2 }}>
                  <Chip label={`Selected Files (${localFilesMeta.length + googleDriveFiles.length} / ${MAX_TOTAL_FILES})`} size="small" />
                </Divider>
                <List dense sx={{ maxHeight: 300, overflowY: 'auto',
                  border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  {localFilesMeta.map((file: LocalFileMeta, index: number) => (
                      <ListItem
                          key={`local-${index}-${file.name}-${file.size}`}
                          secondaryAction={
                            <IconButton edge="end" aria-label={`delete ${file.name}`} onClick={() => handleRemoveLocalFile(index)} size="small" disabled={loading}>
                              <DeleteIcon fontSize="small"/>
                            </IconButton>
                          }
                          sx={{ '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, mb: 0.5 }}
                      >
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                            {getFileIcon(file.extension)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={<Typography variant="body2" noWrap title={file.name}>{file.name}</Typography>}
                            secondary={`${file.extension.toUpperCase()} - ${(file.size / 1024 / 1024).toFixed(2)} MB`}
                        />
                      </ListItem>
                  ))}
                  {googleDriveFiles.map((file: GoogleDriveFile, index: number) => (
                      <ListItem
                          key={`gdrive-${index}-${file.id}`}
                          secondaryAction={
                            <IconButton edge="end" aria-label={`delete ${file.name}`} onClick={() => handleRemoveGoogleDriveFile(index)} size="small" disabled={loading}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                          sx={{ '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, mb: 0.5 }}
                      >
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar sx={{ bgcolor: '#e8f0fe', width: 32, height: 32 }}>
                            {getFileIcon(file.name.split('.').pop()?.toLowerCase() || '')}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={<Typography variant="body2" noWrap title={file.name}>{file.name}</Typography>}
                            secondary={`Google Drive - ${file.mimeType.split('/').pop()?.replace('vnd.google-apps.', '').replace('vnd.openxmlformats-officedocument.', '').split('.').pop()?.toUpperCase() || 'File'} ${file.sizeBytes ? `- ${(file.sizeBytes / 1024 / 1024).toFixed(2)} MB` : '- Size N/A'}`}
                        />
                      </ListItem>
                  ))}
                </List>
              </Box>
          )}

          {hasVideoFile && (
              <Box sx={{ my: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle1" component="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  Custom Instructions for Video
                  <Tooltip title="Provide specific instructions (up to 3, max 100 characters each) for processing video content. These might guide summarization or analysis. Leave blank if not needed.">
                    <InfoIcon color="action" sx={{ ml: 1, fontSize: '1.1rem', cursor: 'help' }} />
                  </Tooltip>
                </Typography>
                {customInstructions.map((instruction, index) => (
                    <TextField
                        key={index}
                        label={`Instruction ${index + 1} (Optional)`}
                        fullWidth
                        margin="dense"
                        value={instruction}
                        onChange={(e) => handleCustomInstructionChange(index, e.target.value)}
                        inputProps={{ maxLength: 100 }}
                        helperText={`${instruction.length}/100 characters`}
                        size="small"
                        disabled={loading}
                    />
                ))}
                {customInstructions.length < 3 && (
                    <Button
                        variant="text"
                        color="primary"
                        size="small"
                        startIcon={<FaPlusCircle />}
                        onClick={addCustomInstructionField}
                        sx={{ mt: 1, textTransform: 'none' }}
                        disabled={loading}
                    >
                      Add another instruction field
                    </Button>
                )}
              </Box>
          )}

          <Box mt={4} sx={{ textAlign: 'center' }}>
            <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                    loading ||
                    !database.database_name.trim() ||
                    !!errors['database_name'] ||
                    !hasFiles
                }
                size="large"
                sx={{ minWidth: 150 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Drive'}
            </Button>
          </Box>
        </Box>
      </Paper>
  );
};

export default DatabaseContentForm;